import React, { useCallback } from "react";
import {
  Editor,
  Element as SlateElement,
  Range,
  Text,
  Transforms,
} from "slate";
import { BlockFormats, CustomText, MarkFormats } from "./types";

interface WysiwygProps {
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  editor: Editor;
}

interface UseWysiwyg {
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  toggleMark(format: MarkFormats): void;
  toggleBlock(format: BlockFormats): void;
  isMarkActive(format: MarkFormats): boolean;
  isBlockActive(format: BlockFormats): boolean;
  isLinkActive(selection?: Range | null): boolean;
  getActiveLinkUrl(selection?: Range | null): string | undefined;
  upsertLink(url: string, selection?: Range | null): void;
  removeLink(selection?: Range | null): void;
}

const LIST_TYPES = new Set(["numberedList", "bulletedList"]);

// Prevent the link mark from carrying onto a new line (and into text typed
// there) when the user presses Enter at/after a link.
export const withLinks = (editor: Editor): Editor => {
  const { insertBreak } = editor;
  editor.insertBreak = () => {
    insertBreak();
    Editor.removeMark(editor, "link");
  };
  return editor;
};

export const useWysiwygEditor = (props: WysiwygProps): UseWysiwyg => {
  const { editor } = props;

  const isMarkActive = useCallback(
    (format: MarkFormats) => {
      const marks = Editor.marks(editor);
      return marks ? marks[format] === true : false;
    },
    [editor]
  );

  const isBlockActive = useCallback(
    (format: BlockFormats) => {
      const { selection } = editor;

      if (!selection) {
        return false;
      }

      const [match] = Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) =>
          !Editor.isEditor(n) && SlateElement.isElement(n) && n.kind === format,
      });

      return Boolean(match);
    },
    [editor]
  );

  const toggleMark = useCallback(
    (format: MarkFormats) => {
      const isActive = isMarkActive(format);

      if (isActive) {
        Editor.removeMark(editor, format);
      } else {
        Editor.addMark(editor, format, true);
      }
    },
    [editor, isMarkActive]
  );

  const toggleBlock = useCallback(
    (format: BlockFormats) => {
      const isActive = isBlockActive(format);
      const isList = LIST_TYPES.has(format);

      Transforms.unwrapNodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          LIST_TYPES.has(n.kind),
        split: true,
      });

      const newProperties: Partial<SlateElement> = {
        // eslint-disable-next-line no-nested-ternary
        kind: isActive ? "paragraph" : isList ? "listItem" : format,
      };

      Transforms.setNodes(editor, newProperties);

      if (!isActive && isList) {
        const block = { kind: format, children: [] };
        Transforms.wrapNodes(editor, block);
      }
    },
    [editor, isBlockActive]
  );

  // Read the link from the actual text nodes in the selection rather than from
  // Editor.marks(): stored marks linger after inserting a link and would bleed
  // onto a fresh caret / new line, wrongly showing the previous link as active.
  const getActiveLinkUrl = useCallback(
    (selection?: Range | null) => {
      const at = selection ?? editor.selection;
      if (!at) {
        return undefined;
      }
      const [entry] = Editor.nodes(editor, {
        at,
        // Ignore empty text nodes: a fresh line can inherit the link mark on an
        // empty leaf, which must not count as an existing link to edit.
        match: (n) =>
          Text.isText(n) &&
          Boolean((n as CustomText).link) &&
          (n as CustomText).text !== "",
      });
      return entry ? (entry[0] as CustomText).link ?? undefined : undefined;
    },
    [editor]
  );

  const isLinkActive = useCallback(
    (selection?: Range | null) => Boolean(getActiveLinkUrl(selection)),
    [getActiveLinkUrl]
  );

  const upsertLink = useCallback(
    (url: string, selection?: Range | null) => {
      if (selection) {
        Transforms.select(editor, selection);
      }

      const currentSelection = editor.selection;
      if (!currentSelection) {
        return;
      }

      if (Range.isCollapsed(currentSelection)) {
        // No text selected: insert the URL itself as the linked text.
        Transforms.insertNodes(editor, { text: url, link: url });
      } else {
        Editor.addMark(editor, "link", url);
      }
    },
    [editor]
  );

  const removeLink = useCallback(
    (selection?: Range | null) => {
      if (selection) {
        Transforms.select(editor, selection);
      }

      const currentSelection = editor.selection;
      if (!currentSelection) {
        return;
      }

      // When the caret is collapsed inside a link, expand to the whole linked
      // text node (Slate merges same-mark runs into a single text node).
      if (Range.isCollapsed(currentSelection)) {
        const [entry] = Editor.nodes(editor, {
          match: (n) => Text.isText(n) && Boolean((n as CustomText).link),
        });
        if (entry) {
          Transforms.select(editor, entry[1]);
        }
      }

      Editor.removeMark(editor, "link");
    },
    [editor]
  );

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (props.onKeyDown) {
        props.onKeyDown(event);
      }

      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case "b": {
            return toggleMark("bold");
          }
          case "i": {
            return toggleMark("italic");
          }
          case "u": {
            return toggleMark("underline");
          }
          default: {
            return;
          }
        }
      }
    },
    [toggleMark, props.onKeyDown]
  );

  return {
    toggleMark,
    toggleBlock,
    isMarkActive,
    isBlockActive,
    isLinkActive,
    getActiveLinkUrl,
    upsertLink,
    removeLink,
    onKeyDown,
  };
};
